import { type FC, type KeyboardEvent, type ReactNode } from "react";
import { Stack, styled } from "@mui/material";
import { useIntl } from "react-intl";

import { useErrorsAndWarnings } from "@/hooks/use-errors-and-warnings";
import { type MachineGroupStatus } from "@/schemas";

import { MachineGroupStatusIcon } from "./machine-group-status-icon";
import { getMachineGroupStatusColor, getMachineGroupStatusMessage } from "./machine-group-status-utils";

interface StatusContainerProps {
	readonly $machineGroupStatus: MachineGroupStatus;
}

const StatusContainer = styled(Stack)<StatusContainerProps>`
	background-color: ${(props) => getMachineGroupStatusColor(props.$machineGroupStatus, props.theme)};
	border: 1px solid ${({ theme }) => theme.palette.divider};
	border-radius: 8px;
	padding: 8px;
	margin: 0;
`;

const StatusText = styled("span")`
	line-height: 1;
`;

interface MachineGroupStatusDisplayContainerProps {
	readonly children: ReactNode;
	readonly machineGroupStatus: MachineGroupStatus;
}

const MachineGroupStatusDisplayContainer: FC<MachineGroupStatusDisplayContainerProps> = ({
	children,
	machineGroupStatus,
}) => {
	const { openModal } = useErrorsAndWarnings();
	const isClickable = machineGroupStatus === "Error";

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (isClickable === false || (event.key !== "Enter" && event.key !== " ")) {
			return;
		}

		event.preventDefault();
		openModal();
	};

	return (
		<StatusContainer
			direction="row"
			alignItems="center"
			$machineGroupStatus={machineGroupStatus}
			gap={0.5}
			onClick={isClickable ? openModal : undefined}
			onKeyDown={isClickable ? handleKeyDown : undefined}
			role={isClickable ? "button" : undefined}
			tabIndex={isClickable ? 0 : undefined}
			sx={isClickable ? { cursor: "pointer" } : undefined}
			data-testid="machine-group-status-display__container"
		>
			{children}
		</StatusContainer>
	);
};

interface MachineGroupStatusDisplayProps {
	readonly machineGroupStatus: MachineGroupStatus;
}

export const MachineGroupStatusDisplay: FC<MachineGroupStatusDisplayProps> = ({ machineGroupStatus }) => {
	const intl = useIntl();

	return (
		<MachineGroupStatusDisplayContainer machineGroupStatus={machineGroupStatus}>
			<MachineGroupStatusIcon status={machineGroupStatus} useColor />
			<StatusText data-cy="mg-status" data-mg-status={machineGroupStatus}>
				{getMachineGroupStatusMessage(machineGroupStatus, intl)}
			</StatusText>
		</MachineGroupStatusDisplayContainer>
	);
};
